// ═══════════════════════════════════════════════════
// UI Store — Sidebar, notifications, scan progress
// ═══════════════════════════════════════════════════

import { create } from 'zustand';
import type { ScanEvent, FindingSeverity, Channel } from '@/types';

interface UIState {
  // Sidebar
  sidebarCollapsed: boolean;
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;

  // Notifications
  unreadCount: number;
  setUnreadCount: (count: number) => void;

  // Active scan progress (SSE driven)
  activeScan: ActiveScanState | null;
  setScanEvent: (event: ScanEvent) => void;
  clearActiveScan: () => void;

  // Command palette (future)
  commandPaletteOpen: boolean;
  toggleCommandPalette: () => void;

  // Onboarding
  onboardingComplete: boolean;
  setOnboardingComplete: (complete: boolean) => void;
}

interface ActiveScanState {
  scanId: string;
  propertyName: string;
  channelCount: number;
  channelsComplete: number;
  currentChannel: Channel | null;
  findings: LiveFinding[];
  scores: { compliance: number | null; consistency: number | null; pulse: number | null };
  status: 'running' | 'complete' | 'error';
}

interface LiveFinding {
  channel: Channel;
  code: string;
  severity: FindingSeverity;
  title: string;
}

export const useUIStore = create<UIState>()((set) => ({
  // Sidebar
  sidebarCollapsed: false,
  toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
  setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),

  // Notifications
  unreadCount: 0,
  setUnreadCount: (count) => set({ unreadCount: count }),

  // Active scan
  activeScan: null,
  setScanEvent: (event) =>
    set((state) => {
      const scan = state.activeScan;

      switch (event.type) {
        case 'scan:started':
          return {
            activeScan: {
              scanId: event.scanId,
              propertyName: event.propertyName,
              channelCount: event.channelCount,
              channelsComplete: 0,
              currentChannel: null,
              findings: [],
              scores: { compliance: null, consistency: null, pulse: null },
              status: 'running',
            },
          };

        case 'channel:started':
          if (!scan) return {};
          return {
            activeScan: { ...scan, currentChannel: event.channel },
          };

        case 'finding:detected':
          if (!scan) return {};
          return {
            activeScan: {
              ...scan,
              findings: [
                ...scan.findings,
                {
                  channel: event.channel,
                  code: event.code,
                  severity: event.severity,
                  title: event.title,
                },
              ],
            },
          };

        case 'channel:complete':
          if (!scan) return {};
          return {
            activeScan: {
              ...scan,
              channelsComplete: scan.channelsComplete + 1,
              currentChannel: null,
            },
          };

        case 'score:updated':
          if (!scan) return {};
          return {
            activeScan: {
              ...scan,
              scores: {
                compliance: event.compliance ?? scan.scores.compliance,
                consistency: event.consistency ?? scan.scores.consistency,
                pulse: event.pulse ?? scan.scores.pulse,
              },
            },
          };

        case 'scan:complete':
          if (!scan) return {};
          return {
            activeScan: {
              ...scan,
              status: 'complete',
              scores: {
                compliance: event.scores.compliance,
                consistency: event.scores.consistency,
                pulse: event.scores.pulse,
              },
            },
          };

        case 'scan:error':
          if (!scan) return {};
          return {
            activeScan: { ...scan, status: 'error' },
          };

        default:
          return {};
      }
    }),
  clearActiveScan: () => set({ activeScan: null }),

  // Command palette
  commandPaletteOpen: false,
  toggleCommandPalette: () =>
    set((s) => ({ commandPaletteOpen: !s.commandPaletteOpen })),

  // Onboarding
  onboardingComplete: false,
  setOnboardingComplete: (complete) => set({ onboardingComplete: complete }),
}));

